import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { reservationAPI, guestAPI, rateAPI } from '@/services/api';
import { toast } from 'sonner';
import { format, differenceInDays, parseISO } from 'date-fns';
import { User, Calendar, DollarSign, Phone, Mail, FileText } from 'lucide-react';

const SOURCES = ['Directo', 'Teléfono', 'WhatsApp', 'Email', 'Walk-in', 'Booking.com', 'Airbnb', 'Expedia', 'Otro'];

export default function CreateBookingModal({ open, onOpenChange, selectedRoom, selectedDate, propertyId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [baseRate, setBaseRate] = useState(0);
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_lastname: '',
    guest_email: '',
    guest_phone: '',
    guest_documento: '',
    fecha_checkin: '',
    fecha_checkout: '',
    num_huespedes: 1,
    precio_por_noche: 0,
    precio_total: 0,
    fuente: 'Directo',
    estado: 'confirmada',
    notas: ''
  });

  useEffect(() => {
    if (selectedDate) {
      const checkIn = format(selectedDate, 'yyyy-MM-dd');
      const checkOut = format(new Date(selectedDate.getTime() + 86400000), 'yyyy-MM-dd'); // +1 day
      setFormData(prev => ({
        ...prev,
        fecha_checkin: checkIn,
        fecha_checkout: checkOut
      }));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (propertyId) {
      fetchBaseRate();
    }
  }, [propertyId]);

  useEffect(() => {
    // Auto calculate total when dates or price change
    if (formData.fecha_checkin && formData.fecha_checkout && formData.precio_por_noche) {
      const nights = differenceInDays(
        parseISO(formData.fecha_checkout),
        parseISO(formData.fecha_checkin)
      );
      if (nights > 0) {
        setFormData(prev => ({
          ...prev,
          precio_total: nights * prev.precio_por_noche
        }));
      }
    }
  }, [formData.fecha_checkin, formData.fecha_checkout, formData.precio_por_noche]);

  const fetchBaseRate = async () => {
    try {
      const response = await rateAPI.getAll(propertyId);
      if (response.data.length > 0) {
        const rate = response.data[0].precio_base;
        setBaseRate(rate);
        setFormData(prev => ({ ...prev, precio_por_noche: rate }));
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRoom) {
      toast.error('Selecciona una habitación');
      return;
    }

    const nights = differenceInDays(
      parseISO(formData.fecha_checkout),
      parseISO(formData.fecha_checkin)
    );

    if (nights <= 0) {
      toast.error('La fecha de checkout debe ser posterior al checkin');
      return;
    }

    setLoading(true);
    try {
      const reservationData = {
        property_id: propertyId,
        room_id: selectedRoom.id,
        ...formData,
        precio_total: nights * formData.precio_por_noche
      };

      await reservationAPI.create(reservationData);
      toast.success('Reserva creada exitosamente');
      onSuccess?.();
      onOpenChange(false);
      
      // Reset form
      setFormData({
        guest_name: '',
        guest_lastname: '',
        guest_email: '',
        guest_phone: '',
        guest_documento: '',
        fecha_checkin: '',
        fecha_checkout: '',
        num_huespedes: 1,
        precio_por_noche: baseRate,
        precio_total: 0,
        fuente: 'Directo',
        estado: 'confirmada',
        notas: ''
      });
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la reserva');
    } finally {
      setLoading(false);
    }
  };

  const nights = formData.fecha_checkin && formData.fecha_checkout 
    ? differenceInDays(parseISO(formData.fecha_checkout), parseISO(formData.fecha_checkin))
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
            <Calendar className="h-5 w-5 text-primary" />
            Nueva Reserva Directa
          </DialogTitle>
          <DialogDescription>
            {selectedRoom && (
              <span className="text-primary font-medium">
                Habitación: {selectedRoom.nombre} ({selectedRoom.tipo})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Guest Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <User className="h-4 w-4" />
              Datos del Huésped
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guest_name">Nombre *</Label>
                <Input
                  id="guest_name"
                  placeholder="Juan"
                  value={formData.guest_name}
                  onChange={(e) => handleChange('guest_name', e.target.value)}
                  required
                  data-testid="booking-guest-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest_lastname">Apellido</Label>
                <Input
                  id="guest_lastname"
                  placeholder="Pérez"
                  value={formData.guest_lastname}
                  onChange={(e) => handleChange('guest_lastname', e.target.value)}
                  data-testid="booking-guest-lastname"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guest_email" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Email *
                </Label>
                <Input
                  id="guest_email"
                  type="email"
                  placeholder="juan@email.com"
                  value={formData.guest_email}
                  onChange={(e) => handleChange('guest_email', e.target.value)}
                  required
                  data-testid="booking-guest-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest_phone" className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> Teléfono *
                </Label>
                <Input
                  id="guest_phone"
                  placeholder="+57 300 123 4567"
                  value={formData.guest_phone}
                  onChange={(e) => handleChange('guest_phone', e.target.value)}
                  required
                  data-testid="booking-guest-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guest_documento" className="flex items-center gap-1">
                <FileText className="h-3 w-3" /> Documento de Identidad
              </Label>
              <Input
                id="guest_documento"
                placeholder="CC 1234567890"
                value={formData.guest_documento}
                onChange={(e) => handleChange('guest_documento', e.target.value)}
                data-testid="booking-guest-doc"
              />
            </div>
          </div>

          {/* Dates and Pricing */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Fechas y Tarifas
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_checkin">Check-in *</Label>
                <Input
                  id="fecha_checkin"
                  type="date"
                  value={formData.fecha_checkin}
                  onChange={(e) => handleChange('fecha_checkin', e.target.value)}
                  required
                  data-testid="booking-checkin"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fecha_checkout">Check-out *</Label>
                <Input
                  id="fecha_checkout"
                  type="date"
                  value={formData.fecha_checkout}
                  onChange={(e) => handleChange('fecha_checkout', e.target.value)}
                  required
                  data-testid="booking-checkout"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="num_huespedes">Huéspedes</Label>
                <Input
                  id="num_huespedes"
                  type="number"
                  min="1"
                  max={selectedRoom?.capacidad || 10}
                  value={formData.num_huespedes}
                  onChange={(e) => handleChange('num_huespedes', parseInt(e.target.value))}
                  data-testid="booking-guests-count"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="precio_por_noche">Precio/Noche ($)</Label>
                <Input
                  id="precio_por_noche"
                  type="number"
                  min="0"
                  step="1000"
                  value={formData.precio_por_noche}
                  onChange={(e) => handleChange('precio_por_noche', parseFloat(e.target.value))}
                  required
                  data-testid="booking-price-night"
                />
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <div className="h-10 px-3 py-2 bg-primary/10 rounded-md flex items-center font-bold text-primary">
                  ${(nights * formData.precio_por_noche).toLocaleString()}
                </div>
              </div>
            </div>

            {nights > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                {nights} {nights === 1 ? 'noche' : 'noches'} × ${formData.precio_por_noche.toLocaleString()} = 
                <span className="font-bold text-primary"> ${(nights * formData.precio_por_noche).toLocaleString()}</span>
              </p>
            )}
          </div>

          {/* Source and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fuente">Fuente de Reserva</Label>
              <Select value={formData.fuente} onValueChange={(value) => handleChange('fuente', value)}>
                <SelectTrigger data-testid="booking-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <Select value={formData.estado} onValueChange={(value) => handleChange('estado', value)}>
                <SelectTrigger data-testid="booking-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmada">Confirmada</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notas">Notas</Label>
            <Textarea
              id="notas"
              placeholder="Notas adicionales sobre la reserva..."
              value={formData.notas}
              onChange={(e) => handleChange('notas', e.target.value)}
              rows={2}
              data-testid="booking-notes"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-primary"
              disabled={loading}
              data-testid="booking-submit-btn"
            >
              {loading ? 'Creando...' : 'Crear Reserva'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
